// pages/DepartmentPage.jsx
import React, { useMemo } from 'react';
import {
  FaUniversity, FaGraduationCap, FaBuilding, FaFileAlt,
  FaToggleOn, FaCogs
} from 'react-icons/fa';
import GenericCrudPage from '../components/GenericCrudPage';
import { useColleges, useDegrees } from '../hooks/useDropdownData';

const DepartmentPage = () => {
  const { colleges, loading: loadingColleges } = useColleges();
  const { degrees, loading: loadingDegrees } = useDegrees();

  const crudConfig = {
    baseUrl: process.env.REACT_APP_BASE_URL || '',
    endpoints: {
      fetch: '/master/getDepartment',
      save: '/master/saveDepartment',
      update: '/master/updateDepartment',
      delete: '/master/deleteDepartment'
    },
    entityName: 'Department',
    initialFormState: {
      exName: '',
      exDescription: '',
      college: '',
      degree: '',
      // Removed exIsActive - will always be true by default
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.exName) errors.exName = 'Department Name is required.';
      if (!form.college) errors.college = 'College is required.';
      if (!form.degree) errors.degree = 'Degree is required.';
      return errors;
    }
  };

  const formFields = useMemo(() => [
    {
      name: 'college', 
      label: 'College', 
      icon: FaUniversity, 
      as: 'select', 
      required: true,
      options: [
        { value: '', label: loadingColleges ? 'Loading...' : 'Select College' },
        ...colleges.map(c => ({ value: c.id, label: c.exName }))
      ]
    },
    {
      name: 'degree', 
      label: 'Degree', 
      icon: FaGraduationCap, 
      as: 'select', 
      required: true,
      options: [
        { value: '', label: loadingDegrees ? 'Loading...' : 'Select Degree' },
        ...degrees.map(d => ({ value: d.id, label: d.exName }))
      ]
    },
    { 
      name: 'exName', 
      label: 'Department Name', 
      icon: FaBuilding, 
      required: true,
      placeholder: 'Enter Department Name'
    },
    { 
      name: 'exDescription', 
      label: 'Description', 
      icon: FaFileAlt, 
      as: 'textarea', 
      rows: 3, 
      fullWidth: true,
      placeholder: 'Enter Description'
    }
    // Removed the switch field - will always be active by default
  ], [colleges, degrees, loadingColleges, loadingDegrees]);

  const tableColumns = [
    {
      key: 'exCollege',
      label: 'College',
      sortable: true,
      render: (item) => {
        // Handle different possible data structures
        if (item.exCollege) return item.exCollege;
        if (item.college?.exName) return item.college.exName;
        if (item.exCollegeId) {
          const college = colleges.find(c => c.id === item.exCollegeId);
          return college ? college.exName : 'N/A';
        }
        return 'N/A';
      }
    },
    {
      key: 'degree',
      label: 'Degree',
      sortable: true,
      render: (item) => {
        // Handle different possible data structures
        if (item.degree && typeof item.degree === 'string') return item.degree;
        if (item.degree?.exName) return item.degree.exName;
        if (item.degreeId) {
          const degree = degrees.find(d => d.id === item.degreeId);
          return degree ? degree.exName : 'N/A';
        }
        return 'N/A';
      }
    },
    { 
      key: 'exDepartmentName', 
      label: 'Department Name', 
      sortable: true,
      render: (item) => item.exDepartmentName || item.exName || 'N/A'
    },
    { 
      key: 'exDescription', 
      label: 'Description', 
      sortable: true,
      render: (item) => item.exDescription || 'N/A'
    }
    // Removed duplicate status column - using the one from shared component
  ];

  // Define CSV headers
  const csvHeaders = [
    { 
      key: 'exCollege', 
      label: 'College',
      render: (item) => {
        if (item.exCollege) return item.exCollege;
        if (item.college?.exName) return item.college.exName;
        if (item.exCollegeId) {
          const college = colleges.find(c => c.id === item.exCollegeId);
          return college ? college.exName : 'N/A';
        }
        return 'N/A';
      }
    },
    { 
      key: 'degree', 
      label: 'Degree',
      render: (item) => {
        if (item.degree && typeof item.degree === 'string') return item.degree;
        if (item.degree?.exName) return item.degree.exName;
        if (item.degreeId) {
          const degree = degrees.find(d => d.id === item.degreeId);
          return degree ? degree.exName : 'N/A';
        }
        return 'N/A';
      }
    },
    { 
      key: 'exDepartmentName', 
      label: 'Department Name',
      render: (item) => item.exDepartmentName || item.exName || 'N/A'
    },
    { key: 'exDescription', label: 'Description' },
    { 
      key: 'exIsActive', 
      label: 'Status', 
      render: (item) => item.exIsActive ? 'Active' : 'Inactive' 
    }
  ];

  const transformPayload = (form, editId, userId) => {
    const payload = {
      colleges: parseInt(form.college, 10), // Note: API expects 'colleges' not 'college'
      degree: parseInt(form.degree, 10),
      exName: form.exName,
      exDescription: form.exDescription,
      exIsActive: 1, // Always set to active
      user: userId,
    };
    
    if (editId) {
      payload.id = editId;
    }
    
    console.log('Department payload being sent:', payload);
    return payload;
  };

  const transformToForm = (item) => ({
    exName: item.exDepartmentName || item.exName || '',
    exDescription: item.exDescription || '',
    college: item.exCollegeId?.toString() || item.college?.id?.toString() || '',
    degree: item.degreeId?.toString() || item.degree?.id?.toString() || '',
    // Removed exIsActive from form state
  });

  const getViewFields = (item) => item ? [
    { 
      label: 'Department Name', 
      value: item.exDepartmentName || item.exName || 'N/A', 
      icon: FaBuilding 
    },
    { 
      label: 'College', 
      value: item.exCollege || 
             item.college?.exName || 
             colleges.find(c => c.id === item.exCollegeId)?.exName || 
             'N/A', 
      icon: FaUniversity 
    },
    { 
      label: 'Degree', 
      value: (typeof item.degree === 'string' ? item.degree : item.degree?.exName) || 
             degrees.find(d => d.id === item.degreeId)?.exName || 
             'N/A', 
      icon: FaGraduationCap 
    },
    { 
      label: 'Status', 
      value: item.exIsActive ? 'Active' : 'Inactive',
      icon: FaToggleOn
    },
    { 
      label: 'Description', 
      value: item.exDescription || 'No description provided', 
      icon: FaFileAlt, 
      fullWidth: true 
    },
  ] : [];

  // Custom data transformer for the fetched data
  const transformFetchedData = (response) => {
    // Handle the response structure from getDepartment endpoint
    if (response.data?.department) {
      return response.data.department;
    }
    return response.data || [];
  };

  return (
    <GenericCrudPage
      title="Department"
      config={crudConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      csvHeaders={csvHeaders}
      csvFilename="departments.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
      transformFetchedData={transformFetchedData}
      emptyIcon={FaBuilding}
      searchPlaceholder="Search departments, colleges, degrees..."
    />
  );
};

export default DepartmentPage;